import React, { memo } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { Modal } from 'Components/Modal';
import { PurpleButton } from 'Components/Button';
import { Icon } from 'Components/Icons';

import classes from './manageTimecardTypesModal.module.css';

interface TimecardType {
  id: number;
  name: string;
  rate?: number;
}

interface CompanyRate {
  id: number;
  timecard_type_id: number;
  rate: number;
}

interface Props {
  isOpen: boolean;
  timecardTypes: TimecardType[];
  companyRates: CompanyRate[];
  defaultLaborRate: number | null;
  newTypeName: string;
  editingTypeId: number | null;
  editingTypeName: string;
  editingTypeRate: string;
  createErrors: any;
  updateErrors: any;
  onClose: () => void;
  onCreateType: () => void;
  onStartEdit: (id: number, name: string) => void;
  onCancelEdit: () => void;
  onDeleteType: (id: number) => void;
  onNewTypeNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEditTypeNameChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEditTypeRateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onDefaultRateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSaveDefaultRate: () => void;
  onSaveBoth: (typeId: number) => void;
}

const ManageTimecardTypesModal = ({
  isOpen,
  timecardTypes,
  companyRates,
  defaultLaborRate,
  newTypeName,
  editingTypeId,
  editingTypeName,
  editingTypeRate,
  createErrors,
  updateErrors,
  onClose,
  onCreateType,
  onStartEdit,
  onCancelEdit,
  onDeleteType,
  onNewTypeNameChange,
  onEditTypeNameChange,
  onEditTypeRateChange,
  onDefaultRateChange,
  onSaveDefaultRate,
  onSaveBoth,
}: Props) => (
  <Modal
    id="manage-timecard-types-modal"
    classes={classes}
    title="Manage Timecard Types"
    isOpen={isOpen}
    modalHeader
    dataBsBackdrop="static"
    dataBsKeyboard="false"
    modalCloseClick={onClose}
  >
    <div className={classes.content}>
      {/* Default Labor Rate Section */}
      <div className={classes.addTypeSection}>
        <div className={classes.sectionLabel}>Default Labor Rate (per hour)</div>
        <div className={classes.inputRow}>
          <input
            type="number"
            className={classes.input}
            placeholder="0.00"
            value={defaultLaborRate ?? ''}
            onChange={onDefaultRateChange}
            min="0"
            step="0.01"
          />
          <PurpleButton
            className={classes.addButton}
            onClick={onSaveDefaultRate}
            disabled={defaultLaborRate === null}
          >
            Save
          </PurpleButton>
        </div>
        <div className={classes.helperText}>Applied when no type-specific rate is set</div>
      </div>

      {/* Add new type */}
      <div className={classes.addTypeSection}>
        <div className={classes.sectionLabel}>Add Timecard Type</div>
        <div className={classes.inputRow}>
          <input
            type="text"
            className={classes.input}
            placeholder="Enter new type name"
            value={newTypeName}
            onChange={onNewTypeNameChange}
            onKeyDown={(e) => e.key === 'Enter' && onCreateType()}
          />
          <PurpleButton
            className={classes.addButton}
            onClick={onCreateType}
            disabled={!newTypeName.trim()}
          >
            Add Type
          </PurpleButton>
        </div>
        {createErrors?.name && (
          <div className={classes.errorText}>{createErrors.name[0]}</div>
        )}
      </div>

      {/* List of types */}
      <div className={classes.typesList}>
        {timecardTypes.length === 0 ? (
          <div className={classes.emptyState}>No timecard types found</div>
        ) : (
          timecardTypes.map((type: TimecardType) => {
            const companyRate = companyRates.find((r) => r.timecard_type_id === type.id);
            const currentRate = editingTypeId === type.id ? editingTypeRate : (companyRate?.rate ?? '');
            return (
              <div key={type.id} className={classes.typeItem}>
                {editingTypeId === type.id ? (
                  <div className={classes.editRow}>
                    <input
                      type="text"
                      className={classes.input}
                      value={editingTypeName}
                      onChange={onEditTypeNameChange}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') onSaveBoth(type.id);
                        if (e.key === 'Escape') onCancelEdit();
                      }}
                      autoFocus
                    />
                    <input
                      type="number"
                      className={`${classes.input} ${classes.rateInput}`}
                      placeholder="Rate"
                      value={currentRate}
                      onChange={onEditTypeRateChange}
                      min="0"
                      step="0.01"
                    />
                    <button
                      type="button"
                      className={classes.iconButton}
                      onClick={() => onSaveBoth(type.id)}
                      title="Save"
                    >
                      <Icon type="checkmark" />
                    </button>
                    <button
                      type="button"
                      className={classes.iconButton}
                      onClick={onCancelEdit}
                      title="Cancel"
                    >
                      <Icon type="close" />
                    </button>
                  </div>
                ) : (
                  <div className={classes.viewRow}>
                    <div className={classes.typeInfo}>
                      <span className={classes.typeName}>{type.name}</span>
                      {companyRate && (
                        <span className={classes.rateBadge}>${Number(companyRate.rate).toFixed(2)}/hr</span>
                      )}
                    </div>
                    <div className={classes.actions}>
                      <button
                        type="button"
                        className={classes.iconButton}
                        onClick={() => onStartEdit(type.id, type.name)}
                        title="Edit"
                      >
                        <Icon type="edit" />
                      </button>
                      <button
                        type="button"
                        className={`${classes.iconButton} ${classes.deleteButton}`}
                        onClick={() => onDeleteType(type.id)}
                        title="Delete"
                      >
                        <Icon type="trash" />
                      </button>
                    </div>
                  </div>
                )}
                {updateErrors?.name && editingTypeId === type.id && (
                  <div className={classes.errorText}>{updateErrors.name[0]}</div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      <div className={classes.footer}>
        <PurpleButton className={classes.closeButton} onClick={onClose}>
          Done
        </PurpleButton>
      </div>
    </div>
  </Modal>
);

const ManageTimecardTypesModalMemo = memo(ManageTimecardTypesModal, areEqual);

export { ManageTimecardTypesModalMemo as ManageTimecardTypesModal };
